import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Search, History, Phone, MessageSquare, CalendarDays, Clock, DollarSign, MapPin, User } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import type { Booking, CustomerData } from '../../types';

interface CustomersTabProps {
  uniqueCustomers: CustomerData[];
  filteredCustomers: CustomerData[];
  customerSearch: string;
  setCustomerSearch: (search: string) => void;
  selectedCustomer: CustomerData | null;
  setSelectedCustomer: (customer: CustomerData | null) => void;
  selectedCustomerBookings: Booking[];
  selectedCustomerStats: { totalSpent: number; completedBookings?: number; count?: number; completed?: number } | null;
  customerStartDate: string | null;
  setCustomerStartDate: (date: string | null) => void;
  customerEndDate: string | null;
  setCustomerEndDate: (date: string | null) => void;
  setShowCustomerDatePicker: (show: boolean) => void;
  setCustomerDatePickerMode: (mode: 'start' | 'end') => void;
  getStatusBadge: (status: string) => React.ReactNode;
}

export const CustomersTab = ({
  uniqueCustomers, filteredCustomers, customerSearch, setCustomerSearch,
  selectedCustomer, setSelectedCustomer, selectedCustomerBookings,
  selectedCustomerStats, customerStartDate, setCustomerStartDate,
  customerEndDate, setCustomerEndDate,
  setShowCustomerDatePicker, setCustomerDatePickerMode,
  getStatusBadge
}: CustomersTabProps) => {
  const { t } = useLanguage();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="text-lg">{t('admin.customerList')}</CardTitle>
          <CardDescription>{uniqueCustomers.length} {t('admin.uniqueCustomers')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('admin.searchCustomer')}
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="pl-10"
              data-testid="customer-search"
            />
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredCustomers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('admin.noCustomersFound')}</p>
            ) : (
              filteredCustomers.map((customer) => (
                <div
                  key={customer.contact_number || customer.name}
                  onClick={() => setSelectedCustomer(customer)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedCustomer?.contact_number === customer.contact_number
                      ? 'bg-pink-100 border-2 border-pink-300'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  data-testid={`customer-item-${customer.contact_number || customer.name}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-pink-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-pink-700" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{customer.name}</p>
                        <p className="text-xs text-gray-500">{customer.contact_number}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {customer.bookings.length} {t('common.bookings')}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-lg">
            {selectedCustomer ? `${selectedCustomer.name} - ${t('admin.customerHistory')}` : t('admin.selectCustomer')}
          </CardTitle>
          {selectedCustomer && (
            <CardDescription>
              {selectedCustomer.bookings.length} {t('common.bookings')} | ฿{selectedCustomer.totalSpent.toLocaleString()} {t('admin.spent')}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {!selectedCustomer ? (
            <div className="text-center py-12 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>{t('admin.selectCustomer')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-2">{t('admin.contactInfo')}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {selectedCustomer.contact_number || 'N/A'}
                  </div>
                  {selectedCustomer.line_id && (
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-green-500" />
                      LINE: {selectedCustomer.line_id}
                    </div>
                  )}
                  {selectedCustomer.whatsapp_number && (
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-green-500" />
                      WhatsApp: {selectedCustomer.whatsapp_number}
                    </div>
                  )}
                </div>
                <div className="mt-3 flex gap-4 text-sm">
                  <span className="text-blue-600">{selectedCustomer.completedBookings} {t('admin.completedLabel')}</span>
                  <span className="text-green-600">฿{selectedCustomer.totalSpent.toLocaleString()} {t('admin.totalSpent')}</span>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3">{t('admin.filterByDateRange')}</h4>
                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="outline" size="sm" className="w-36 justify-start text-left font-normal"
                    onClick={() => { setShowCustomerDatePicker(true); setCustomerDatePickerMode('start'); }}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {customerStartDate ? new Date(customerStartDate).toLocaleDateString() : t('admin.selectStartDate')}
                  </Button>
                  <span className="text-gray-400">{t('admin.to')}</span>
                  <Button variant="outline" size="sm" className="w-36 justify-start text-left font-normal"
                    onClick={() => { setShowCustomerDatePicker(true); setCustomerDatePickerMode('end'); }}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {customerEndDate ? new Date(customerEndDate).toLocaleDateString() : t('admin.selectEndDate')}
                  </Button>
                  {(customerStartDate || customerEndDate) && (
                    <Button variant="ghost" size="sm"
                      onClick={() => { setCustomerStartDate(null); setCustomerEndDate(null); }}>{t('admin.clear')}</Button>
                  )}
                </div>
                {(customerStartDate || customerEndDate) && (
                  <div className="mt-2 text-sm text-blue-700">
                    {t('admin.showingBookings')} {selectedCustomerStats.count} {t('common.bookings')} | {selectedCustomerStats.completed} {t('admin.completedLabel')} | ฿{selectedCustomerStats.totalSpent.toLocaleString()} {t('admin.spent')}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <h4 className="font-medium">{t('admin.bookingHistory')}</h4>
                <span className="text-sm text-gray-500">{selectedCustomerBookings.length} {t('common.bookings')}</span>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {selectedCustomerBookings.map((booking) => (
                  <div key={booking.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">
                        {new Date(booking.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      {getStatusBadge(booking.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {booking.service_type} ({booking.duration_minutes}{t('common.min')})
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        ฿{booking.price_thb}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {booking.hotel_name}, {t('admin.room')} {booking.room_number}
                      </div>
                      {booking.assigned_therapist_name && (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {booking.assigned_therapist_name}
                        </div>
                      )}
                    </div>
                    {booking.special_requests && (
                      <p className="text-xs text-gray-500 mt-2 italic">{t('admin.note')}: {booking.special_requests}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
